/**
 * CSV Preview Canvas
 * Renders CSV geometry data as a preview in the mapping modal
 */

import { useEffect, useRef } from 'react'
import type { GroupedLayer } from '../../../../map-editor-features/csv/lib/layerGrouper'
import type { ObjectType } from '../../../../map-editor-shared/store/objectTypeStore'

interface CSVPreviewCanvasProps {
  groupedLayers: GroupedLayer[]
  layerMappings?: Record<string, string>
  objectTypes?: ObjectType[]
  selectedLayer: string | null
}

interface Point {
  x: number
  y: number
}

export function CSVPreviewCanvas({ groupedLayers, layerMappings = {}, objectTypes = [], selectedLayer }: CSVPreviewCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Calculate bounds of all points
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity

    groupedLayers.forEach(layer => {
      layer.entities.forEach(entity => {
        entity.points.forEach(point => {
          minX = Math.min(minX, point.x)
          minY = Math.min(minY, point.y)
          maxX = Math.max(maxX, point.x)
          maxY = Math.max(maxY, point.y)
        })
      })
    })

    // Add padding
    const padding = 20
    let dataWidth = maxX - minX
    let dataHeight = maxY - minY

    // Avoid division by zero when all points are same or no points
    if (dataWidth <= 0 || !Number.isFinite(dataWidth)) dataWidth = 1
    if (dataHeight <= 0 || !Number.isFinite(dataHeight)) dataHeight = 1

    // Scale to fit canvas (allow scale-up so small coordinate ranges fill the preview)
    const scaleX = (canvas.width - padding * 2) / dataWidth
    const scaleY = (canvas.height - padding * 2) / dataHeight
    const scale = Math.min(scaleX, scaleY)

    // Calculate offset to center the drawing
    const offsetX = padding + (canvas.width - padding * 2 - dataWidth * scale) / 2
    const offsetY = padding + (canvas.height - padding * 2 - dataHeight * scale) / 2

    // Transform function
    const transform = (point: Point): Point => ({
      x: offsetX + (point.x - minX) * scale,
      y: canvas.height - (offsetY + (point.y - minY) * scale) // Flip Y axis
    })

    // Get color for layer
    const getLayerColor = (layerName: string): string => {
      const typeId = layerMappings[layerName]
      if (!typeId) return '#666666' // Unmapped layers are gray

      const type = objectTypes.find(t => t.id === typeId)
      if (!type) return '#666666'

      // If type has icon color code, use it
      if (type.icon && type.icon.startsWith('#')) {
        return type.icon
      }

      // Otherwise use type color
      return type.color || '#3B82F6'
    }

    // Render each layer
    groupedLayers.forEach(layer => {
      const isSelected = selectedLayer === layer.layer
      const baseColor = getLayerColor(layer.layer)

      // Change color when selected - bright purple to match the selection color
      const color = isSelected ? '#a78bfa' : baseColor // Bright purple for selected

      layer.entities.forEach(entity => {
        if (entity.points.length === 0) return

        // Dim unselected layers when a layer is selected
        const alpha = selectedLayer && !isSelected ? 0.15 : 1

        ctx.strokeStyle = color
        ctx.fillStyle = color
        ctx.lineWidth = isSelected ? 5 : 2
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        ctx.globalAlpha = alpha

        const transformedPoints = entity.points.map(transform)

        // Check if it's a closed polygon (LWPOLYLINE or closed path; use epsilon for float)
        const EPS = 1e-6
        const first = entity.points[0]
        const last = entity.points[entity.points.length - 1]
        const isClosed = entity.entityType === 'LWPOLYLINE' ||
                        (entity.points.length > 2 &&
                         Math.abs(first.x - last.x) < EPS &&
                         Math.abs(first.y - last.y) < EPS)

        // Draw path
        ctx.beginPath()
        transformedPoints.forEach((point, index) => {
          if (index === 0) {
            ctx.moveTo(point.x, point.y)
          } else {
            ctx.lineTo(point.x, point.y)
          }
        })

        if (isClosed) {
          ctx.closePath()
          // Fill with semi-transparent color
          const fillAlpha = isSelected ? 0.4 : 0.2
          ctx.globalAlpha = alpha * fillAlpha
          ctx.fill()
          ctx.globalAlpha = alpha
        }

        ctx.stroke()

        // Draw glow effect for selected layer
        if (isSelected) {
          ctx.shadowColor = '#a78bfa'
          ctx.shadowBlur = 15
          ctx.stroke()
          ctx.shadowBlur = 0
        }

        // Draw point entities with enhanced visibility
        // Check if entity is a point (single point, zero-size, or flagged as point)
        const isPointEntity = entity.isPoint ||
                             entity.points.length === 1 ||
                             (transformedPoints.length > 1 &&
                              Math.abs(transformedPoints[0].x - transformedPoints[transformedPoints.length - 1].x) < 1 &&
                              Math.abs(transformedPoints[0].y - transformedPoints[transformedPoints.length - 1].y) < 1)

        if (isPointEntity) {
          // Get center point for multi-point entities that collapse to a point
          const centerX = transformedPoints.reduce((sum, p) => sum + p.x, 0) / transformedPoints.length
          const centerY = transformedPoints.reduce((sum, p) => sum + p.y, 0) / transformedPoints.length

          // Draw point as a circle with icon-like appearance
          const pointRadius = isSelected ? 8 : 6

          // Outer circle
          ctx.beginPath()
          ctx.arc(centerX, centerY, pointRadius, 0, Math.PI * 2)
          ctx.fillStyle = color
          ctx.fill()

          // Inner white dot for visibility
          ctx.beginPath()
          ctx.arc(centerX, centerY, 2, 0, Math.PI * 2)
          ctx.fillStyle = '#ffffff'
          ctx.fill()
        }
      })
    })

    // Reset alpha
    ctx.globalAlpha = 1

  }, [groupedLayers, layerMappings, objectTypes, selectedLayer])

  return (
    <canvas
      ref={canvasRef}
      width={600}
      height={400}
      style={{
        width: '100%',
        height: '100%',
        border: '1px solid var(--local-border)',
        borderRadius: '8px',
        background: 'var(--color-canvas-bg)'
      }}
    />
  )
}
